import { useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * Re-read the chat lists whenever the page comes back to the foreground.
 *
 * Live previews arrive over the realtime socket, and a socket can be dropped -- most
 * reliably by backgrounding the app, which is also exactly when the interesting messages
 * arrive. This is the same recovery the open thread already does
 * (useChatMessages.ts:98-107), minus its 5s poll: a thread poll is one cheap query, where
 * these are three multi-table list reads, and a message that lands while the list is on
 * screen comes in over realtime anyway.
 *
 * It doubles as how threads created while the viewer was away turn up at all -- realtime
 * can describe a message, but not a row the client has never fetched.
 */
export function useChatListResync(resync: () => void, enabled: boolean): void {
  // Held in a ref so a caller that rebuilds the callback each render does not tear the
  // listeners down and put them back on every commit.
  const resyncRef = useRef(resync);
  resyncRef.current = resync;

  useEffect(() => {
    if (!enabled) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') resyncRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);

    // Native resume is announced explicitly rather than relying on the WebView firing
    // visibilitychange, matching usePresenceHeartbeat.
    const appStateListener = Capacitor.isNativePlatform()
      ? CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) resyncRef.current();
        })
      : null;

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      void appStateListener?.then((listener) => listener.remove());
    };
  }, [enabled]);
}
